/**
 * Hint sutil: las palabras del coach son clickeables (abren su significado).
 *
 * Aviso descartable que vive encima de la barra del micrófono. Frecuencia:
 * aparece como mucho una vez por día y, tras cerrarlo 3 veces, no vuelve a
 * mostrarse nunca más (se asume aprendido). Se oculta cuando hay otra tarjeta
 * flotante abierta (p. ej. sugerencias) para evitar solapamiento — el padre
 * pasa `hidden`.
 */
import { useState } from 'react';
import { IconXSmall } from '../icons.js';

const TAPWORD_HINT_KEY = 'orborix-tapword-hint-v2';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 3;

interface HintState {
  /** Cuántas veces lo cerró el usuario. */
  count: number;
  /** Timestamp del último cierre (ms). */
  ts: number;
}

function readState(): HintState {
  try {
    const raw = localStorage.getItem(TAPWORD_HINT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<HintState>;
      return { count: Number(parsed.count) || 0, ts: Number(parsed.ts) || 0 };
    }
  } catch {
    /* ignore */
  }
  return { count: 0, ts: 0 };
}

function shouldShow(s: HintState): boolean {
  if (s.count >= MAX_DISMISSALS) return false;
  return Date.now() - s.ts >= ONE_DAY_MS;
}

const IconTapFinger = ({ size = 16 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 11 V 5.5 a 1.6 1.6 0 0 1 3.2 0 V 11" />
    <path d="M12.2 11 V 8.6 a 1.5 1.5 0 0 1 3 0 V 11" />
    <path d="M15.2 11 V 9.4 a 1.5 1.5 0 0 1 3 0 V 15 a 5 5 0 0 1 -5 5 h -1.4 a 4 4 0 0 1 -2.9 -1.3 L 5.4 15.4 a 1.5 1.5 0 0 1 2.2 -2 L 9 15" />
  </svg>
);

interface TapWordHintProps {
  /** Ocultar mientras otra tarjeta flotante esté abierta (sugerencias). */
  hidden?: boolean;
}

export function TapWordHint({ hidden = false }: TapWordHintProps) {
  const [visible, setVisible] = useState(() => shouldShow(readState()));

  const dismiss = (): void => {
    try {
      const prev = readState();
      const next: HintState = { count: prev.count + 1, ts: Date.now() };
      localStorage.setItem(TAPWORD_HINT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible || hidden) return null;

  return (
    <div className="tap-hint slide-up">
      <span className="tap-hint-ic">
        <IconTapFinger size={16} />
      </span>
      <span className="tap-hint-tx">
        Tocá las palabras del <b>coach</b> para ver su significado
      </span>
      <button
        className="tap-hint-x"
        onClick={dismiss}
        aria-label="Entendido, no mostrar de nuevo"
        title="Entendido"
      >
        <IconXSmall size={11} />
      </button>
    </div>
  );
}
